import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, FilterChips, Modal, Radio } from '@sendible/design-system';
import type { FilterChipData } from '@sendible/design-system';
import { AdvancedSelect, Option } from '@sendible/design-system/src/components/molecules/AdvancedSelect';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { DateTime } from 'luxon';
import { sanitizeString } from '@sendible/common';
import { useGetFilters } from '@sendible/frontendv2/src/models/filters';
import { Content, CustomDateRow, Footer, FormatGroup, PeriodList, PeriodOption, Section, SectionLabel } from './index.styles';
import { useCalendarContext } from '../../context';
import {
  exportCalendarSectionHtml,
  exportDailyCurrentViewHtml,
  exportFilteredDateRangeCsv,
  exportFilteredMessagesHtml,
  exportMonthlySummaryCsv,
  exportRangeMessagesCsv,
  exportWeekSummaryCsv,
  printFilteredMessages,
} from '../../exportUtils';
import type { ExportCredentials, ExportStatusFilter } from '../../exportUtils';
import { addMonths, getStatusIcon, keyFromDate, monthStart, networkIconNameMap, startOfWeek } from '../../utils';

type ExportFormat = 'csv' | 'pdf' | 'html';
type ExportView = 'current' | 'scheduled' | 'sent' | 'failed' | 'queues';
type PeriodType = 'next_month' | 'next_week' | 'current_week' | 'last_month' | 'last_week' | 'today' | 'custom';

type FilterItemWithExtras = FilterItem & {
  networks?: MessageChannels[];
  showAsAvatar?: boolean;
};

const API_PARAM_TO_FILTER_KEY: Record<string, string> = {
  userIds: 'users',
  profileIds: 'profiles',
  profileListIds: 'profileLists',
  queueIds: 'queues',
  campaignIds: 'campaigns',
};

const VIEW_OPTIONS: Option[] = [
  { label: 'current_view', action: 'current' },
  { label: 'scheduled_posts', action: 'scheduled' },
  { label: 'sent_posts', action: 'sent' },
  { label: 'failed_posts', action: 'failed' },
  { label: 'queues', action: 'queues' },
];

export interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportModal = ({ isOpen, onClose }: ExportModalProps) => {
  const { t, i18n } = useTranslation('calendar');
  const user = useBridgeUser();
  const { currentDate, viewMode, userWeekStartDay, filterParams, todayDate, isExporting, setIsExporting } = useCalendarContext();
  const { data: filtersData } = useGetFilters([]);

  const [selectedView, setSelectedView] = useState<ExportView>('current');
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('last_month');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedQueueId, setSelectedQueueId] = useState<string>('');

  const showPeriodSection = selectedView === 'scheduled' || selectedView === 'sent' || selectedView === 'failed' || selectedView === 'queues';
  const showQueueSection = selectedView === 'queues';

  const datePickerMode =
    selectedView === 'scheduled' || selectedView === 'queues' ? 'future' : selectedView === 'failed' || selectedView === 'sent' ? 'past' : 'free';

  const queueOptions = useMemo((): Option[] => {
    const data = filtersData as Record<string, FilterItem[]> | undefined;

    return (data?.queues ?? []).map((q) => ({ label: q.label ?? '', action: String(q.id) }));
  }, [filtersData]);

  const selectedQueueOption = useMemo(
    () => queueOptions.find((o) => o.action === selectedQueueId) ?? queueOptions[0],
    [queueOptions, selectedQueueId]
  );

  const handleClose = useCallback(() => {
    setIsExporting(false);
    setSelectedView('current');
    setSelectedFormat('csv');
    setSelectedPeriod('last_month');
    setCustomStartDate('');
    setCustomEndDate('');
    setSelectedQueueId('');
    onClose();
  }, [onClose]);

  const translatedViewOptions = useMemo(() => VIEW_OPTIONS.map((opt) => ({ label: t(opt.label) as string, action: opt.action })), [t]);

  const selectedViewOption = useMemo(() => translatedViewOptions.find((o) => o.action === selectedView), [translatedViewOptions, selectedView]);

  const filterChips = useMemo((): FilterChipData[] => {
    const chips: FilterChipData[] = [];

    if (!filterParams || !filtersData) return chips;

    const data = filtersData as Record<string, FilterItemWithExtras[]>;

    Object.entries(filterParams).forEach(([paramKey, paramValue]) => {
      if (!paramValue) return;

      const selectedIds = paramValue.split(',');
      const filterKey = API_PARAM_TO_FILTER_KEY[paramKey] || paramKey;
      const items = data[filterKey] ?? [];

      const subItems = selectedIds
        .map((id) => {
          const item = items.find((it) => String(it.id) === id);

          if (!item) return { id, label: id };

          const isProfile = filterKey === 'profiles';
          const isQueue = filterKey === 'queues';
          const networkKey = isProfile ? (item.networks?.[0] ?? '') : item.id;
          const statusIcon = filterKey === 'statuses' ? getStatusIcon(item.id as CalendarStatuses, false) : undefined;
          const iconName = statusIcon?.icon || networkIconNameMap[networkKey];
          const workspace = isQueue ? (item.metadata?.workspace as { color?: string; name?: string } | undefined) : undefined;

          return {
            id: item.id,
            label: (item.label || t(item.id)) as string,
            icon: iconName as FilterChipData['items'][number]['icon'],
            iconColor: statusIcon?.color,
            imageUrl: isProfile ? item.icon : undefined,
            showAsAvatar: isProfile,
            avatarColor: workspace?.color,
            avatarLabel: workspace?.name,
          };
        })
        .filter(Boolean);

      chips.push({
        id: filterKey,
        label: filterKey === 'text' ? (t('contains', { ns: 'calendar' }) as string) : (t(filterKey) as string),
        items: subItems,
        selectedIds,
      });
    });

    return chips;
  }, [filterParams, filtersData, t]);

  const defaultCustomRange = useMemo(() => {
    const end = DateTime.fromISO(todayDate).plus({ days: 30 }).toJSDate();
    const start = DateTime.fromISO(todayDate).minus({ days: 30 }).toJSDate();

    if (datePickerMode === 'future') return { start: todayDate, end: keyFromDate(end) };

    return { start: keyFromDate(start), end: todayDate };
  }, [todayDate, datePickerMode]);

  const getExportDateRange = useCallback(
    (period: PeriodType): { start: string; end: string } => {
      const today = new Date();

      switch (period) {
        case 'next_month': {
          const first = monthStart(addMonths(today, 1));
          const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);

          return { start: keyFromDate(first), end: keyFromDate(last) };
        }
        case 'last_month': {
          const first = monthStart(addMonths(today, -1));
          const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);

          return { start: keyFromDate(first), end: keyFromDate(last) };
        }
        case 'next_week': {
          const anchor = new Date(today);

          anchor.setDate(today.getDate() + 7);

          const ws = startOfWeek(keyFromDate(anchor) as CalendarMonthString, userWeekStartDay);
          const we = new Date(ws);

          we.setDate(ws.getDate() + 6);

          return { start: keyFromDate(ws), end: keyFromDate(we) };
        }
        case 'last_week': {
          const anchor = new Date(today);

          anchor.setDate(today.getDate() - 7);

          const ws = startOfWeek(keyFromDate(anchor) as CalendarMonthString, userWeekStartDay);
          const we = new Date(ws);

          we.setDate(ws.getDate() + 6);

          return { start: keyFromDate(ws), end: keyFromDate(we) };
        }
        case 'current_week': {
          const ws = startOfWeek(keyFromDate(today) as CalendarMonthString, userWeekStartDay);
          const we = new Date(ws);

          we.setDate(ws.getDate() + 6);

          return { start: keyFromDate(ws), end: keyFromDate(we) };
        }
        case 'today': {
          const d = keyFromDate(today);

          return { start: d, end: d };
        }
        case 'custom':
          return {
            start: customStartDate || defaultCustomRange.start,
            end: customStartDate && !customEndDate ? customStartDate : customEndDate || defaultCustomRange.end,
          };
        default:
          return { start: '', end: '' };
      }
    },
    [userWeekStartDay, customStartDate, customEndDate, defaultCustomRange]
  );

  const removeFilterChipMap: Record<string, string> = {
    failed: 'statuses',
    sent: 'statuses',
    scheduled: 'statuses',
    queues: 'queues',
  };

  const filteredChips = filterChips.filter((item) => item.id !== removeFilterChipMap[selectedView]);

  const handleExport = useCallback(async () => {
    if (selectedView === 'current') {
      if (selectedFormat === 'pdf') {
        const style = document.createElement('style');

        style.textContent = '._pendo-badge, ._pendo-badge * { display: none !important; }';
        document.head.appendChild(style);
        window.print();
        document.head.removeChild(style);
      } else if (selectedFormat === 'html') {
        if (viewMode === 'daily') {
          const builtFilterChipsHtml = filteredChips
            .map((chip) => {
              const itemsText = chip.items.map((item) => sanitizeString(item.label)).join(', ');

              return `<span class="filter-chip"><b>${sanitizeString(chip.label)}</b>&nbsp;${itemsText}</span>`;
            })
            .join('');

          exportDailyCurrentViewHtml(t('current_view') as string, builtFilterChipsHtml, filterParams);
        } else {
          exportCalendarSectionHtml('[data-calendar-root]');
        }
      } else if (viewMode === 'monthly') {
        exportMonthlySummaryCsv(currentDate);
      } else if (viewMode === 'weekly') {
        exportWeekSummaryCsv(currentDate, userWeekStartDay);
      } else {
        exportRangeMessagesCsv(currentDate, filterParams);
      }

      onClose();

      return;
    }

    const { start, end } = getExportDateRange(selectedPeriod);

    if (!start || !end) {
      onClose();

      return;
    }

    const statusFilter: ExportStatusFilter | undefined =
      selectedView === 'scheduled' ? 'scheduled' : selectedView === 'sent' ? 'sent' : selectedView === 'failed' ? 'failed' : undefined;

    const queueId = selectedView === 'queues' ? (selectedQueueOption?.action ?? '') : undefined;
    const viewTitle =
      selectedView === 'queues' ? `${selectedViewOption?.label} (${selectedQueueOption?.label})` : (selectedViewOption?.label ?? 'Export');

    const credentials: ExportCredentials = {
      accessToken: user?.accessToken ?? '',
      userId: user?.userId ?? 0,
      email: user?.email ?? '',
      isSwitchedUser: user?.isSwitchedUser,
      username: user?.username,
    };

    const builtFilterChipsHtml = filteredChips
      .map((chip) => {
        const itemsText = chip.items.map((item) => sanitizeString(item.label)).join(', ');

        return `<span class="filter-chip"><b>${sanitizeString(chip.label)}</b>&nbsp;${itemsText}</span>`;
      })
      .join('');

    setIsExporting(true);

    try {
      if (selectedFormat === 'csv') {
        await exportFilteredDateRangeCsv(start, end, credentials, filterParams, statusFilter, queueId);
      } else if (selectedFormat === 'html') {
        await exportFilteredMessagesHtml(start, end, viewTitle, credentials, filterParams, statusFilter, queueId, builtFilterChipsHtml);
      } else if (selectedFormat === 'pdf') {
        await printFilteredMessages(start, end, viewTitle, credentials, filterParams, statusFilter, queueId, builtFilterChipsHtml);
      }
    } finally {
      setIsExporting(false);
    }

    onClose();
  }, [
    selectedFormat,
    selectedView,
    selectedPeriod,
    currentDate,
    viewMode,
    userWeekStartDay,
    selectedQueueOption,
    selectedViewOption,
    filterParams,
    filteredChips,
    user,
    getExportDateRange,
    onClose,
  ]);

  const handleCustomDateChange = useCallback((start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          close={handleClose}
          headingTitle={t('export') as string}
          testId="export-modal"
          variation="medium"
        >
          <Content>
            {filteredChips.length > 0 && (
              <Section>
                <SectionLabel>{t('applied_filters')}</SectionLabel>
                <FilterChips
                  chips={filteredChips}
                  testId="export-filter-chips"
                />
              </Section>
            )}

            <Section>
              <SectionLabel>{t('view')}</SectionLabel>
              <AdvancedSelect
                id="export-view-select"
                fitList
                label={selectedViewOption?.label ?? ''}
                onSelect={(option) => {
                  const newView = option.action as ExportView;
                  const wasScheduled = selectedView === 'scheduled' || selectedView === 'queues';
                  const willBeScheduled = newView === 'scheduled' || newView === 'queues';

                  if (wasScheduled !== willBeScheduled) {
                    setSelectedPeriod(willBeScheduled ? 'next_month' : 'last_month');
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                  setSelectedView(newView);
                }}
                options={translatedViewOptions}
                selectedOptions={selectedViewOption ? [selectedViewOption] : []}
                size="md"
                testId="export-view-select"
              />
            </Section>

            {showQueueSection && (
              <Section>
                <SectionLabel>{t('queue')}</SectionLabel>
                <AdvancedSelect
                  id="export-queue-select"
                  fitList
                  label={selectedQueueOption?.label ?? ''}
                  onSelect={(option) => setSelectedQueueId(option.action)}
                  options={queueOptions}
                  selectedOptions={selectedQueueOption ? [selectedQueueOption] : []}
                  size="md"
                  testId="export-queue-select"
                />
              </Section>
            )}

            {showPeriodSection && (
              <Section>
                <SectionLabel>{t('period')}</SectionLabel>
                <PeriodList>
                  {datePickerMode === 'future' ? (
                    <>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'next_month'}
                        onClick={() => setSelectedPeriod('next_month')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'next_month'}
                          label={t('next_month') as string}
                          size="sm"
                        />
                      </PeriodOption>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'next_week'}
                        onClick={() => setSelectedPeriod('next_week')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'next_week'}
                          label={t('next_week') as string}
                          size="sm"
                        />
                      </PeriodOption>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'current_week'}
                        onClick={() => setSelectedPeriod('current_week')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'current_week'}
                          label={t('current_week') as string}
                          size="sm"
                        />
                      </PeriodOption>
                    </>
                  ) : (
                    <>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'last_month'}
                        onClick={() => setSelectedPeriod('last_month')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'last_month'}
                          label={t('last_month') as string}
                          size="sm"
                        />
                      </PeriodOption>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'last_week'}
                        onClick={() => setSelectedPeriod('last_week')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'last_week'}
                          label={t('last_week') as string}
                          size="sm"
                        />
                      </PeriodOption>
                      <PeriodOption
                        $isSelected={selectedPeriod === 'today'}
                        onClick={() => setSelectedPeriod('today')}
                      >
                        <Radio
                          groupName="export-period"
                          isChecked={selectedPeriod === 'today'}
                          label={t('today') as string}
                          size="sm"
                        />
                      </PeriodOption>
                    </>
                  )}
                  <PeriodOption
                    $isSelected={selectedPeriod === 'custom'}
                    onClick={() => setSelectedPeriod('custom')}
                  >
                    <Radio
                      groupName="export-period"
                      isChecked={selectedPeriod === 'custom'}
                      label=""
                      size="sm"
                    />
                    <CustomDateRow
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPeriod('custom');
                      }}
                    >
                      <DatePicker
                        key={`datepicker-${selectedPeriod}`}
                        defaultOpen={selectedPeriod === 'custom'}
                        endDate={customEndDate || defaultCustomRange.end}
                        locale={i18n.language}
                        maxRangeDays={120}
                        onChange={handleCustomDateChange}
                        placeholder={t('select_date_range') as string}
                        startDate={customStartDate || defaultCustomRange.start}
                        testId="export-custom-date"
                        mode={datePickerMode}
                      />
                    </CustomDateRow>
                  </PeriodOption>
                </PeriodList>
              </Section>
            )}

            <Section>
              <SectionLabel>{t('format')}</SectionLabel>
              <FormatGroup>
                <Button
                  data-active={selectedFormat === 'csv' ? 'true' : undefined}
                  data-testid="export-format-csv"
                  label="CSV"
                  onClick={() => setSelectedFormat('csv')}
                  size={12}
                  customClass={selectedFormat === 'csv' ? 'button-active' : undefined}
                />
                <Button
                  data-active={selectedFormat === 'html' ? 'true' : undefined}
                  data-testid="export-format-html"
                  label="HTML"
                  onClick={() => setSelectedFormat('html')}
                  size={12}
                  customClass={selectedFormat === 'html' ? 'button-active' : undefined}
                />
              </FormatGroup>
            </Section>

            <Footer>
              <Button
                appearance="default"
                data-testid="export-cancel"
                disabled={isExporting}
                label={t('cancel', { ns: 'common_terms' }) as string}
                onClick={handleClose}
                size={14}
                variation="outline"
              />
              <Button
                appearance="primary"
                data-testid="export-submit"
                disabled={isExporting}
                isLoading={isExporting}
                label={t('export') as string}
                onClick={handleExport}
                size={14}
                variation="fill"
              />
            </Footer>
          </Content>
        </Modal>
      )}
    </AnimatePresence>
  );
};
